import SlideFrame from "@/components/SlideFrame";
import { Award, ArrowRight } from "lucide-react";
import { packFor } from "@/lib/industryPack";

/* ============================ 01 · Title (Hero) ============================ */
export function S_Title({ idx, total, prospect }) {
  const prospectName = prospect?.company_name;
  const pack = packFor(prospect);
  const corp = pack.corporate;
  const pills = pack.titlePricingPills;

  return (
    <SlideFrame idx={idx} total={total} kicker={prospect ? `Prepared for ${prospect.company_name}` : "Welcome"} dark>
      <div className="grid grid-cols-12 gap-8 lg:gap-12 items-center pt-6 md:pt-8 pb-12">
        {/* LEFT — Messaging */}
        <div className="col-span-12 md:col-span-8 stagger">
          {prospect ? (
            <div className="flex items-center gap-4 mb-6">
              {prospect.logo_url && (
                <img src={prospect.logo_url} alt={prospect.company_name}
                     className="h-14 md:h-16 max-w-[240px] object-contain bg-white rounded-md p-2 border border-ink-900/8"
                     data-testid="title-prospect-logo" />
              )}
              <div>
                <p className="mono text-[10px] uppercase tracking-[0.28em] text-sun-700">/ prepared specifically for</p>
                <div className="serif text-2xl md:text-3xl text-ink-900 leading-tight mt-0.5" data-testid="title-prospect-name">
                  {prospect.company_name}
                </div>
              </div>
            </div>
          ) : (
            <p className="mono text-[11px] uppercase tracking-[0.28em] text-sun-700 mb-5">/ partner pitch · 2026</p>
          )}

          {/* StoryBrand hero — the visitor's story, not ours */}
          {pack.lawFirm && pack.titleHeadline ? (
            <h1 className="serif font-light text-[6.5vw] md:text-[4.5vw] lg:text-[4rem] xl:text-[4.5rem] leading-[1.04] tracking-tight">
              {pack.titleHeadline.lead}{" "}
              <em className="italic text-sun-600">{pack.titleHeadline.italic}</em>{" "}
              {pack.titleHeadline.trail}
            </h1>
          ) : (
            <h1 className="serif font-light text-[6.5vw] md:text-[4.5vw] lg:text-[4rem] xl:text-[4.5rem] leading-[1.04] tracking-tight">
              You know compliance and legal{" "}
              <em className="italic text-sun-600">can be better.</em>
              {" "}More transparent. More automated. That's where we come in.
            </h1>
          )}

          <p className="mt-7 max-w-3xl text-lg md:text-xl text-ink-900/75 leading-relaxed">
            {(corp || pack.lawFirm || pack.titleSubline) ? (
              <>{prospectName && !pack.lawFirm ? `${prospectName}, ` : ""}{pack.titleSubline}</>
            ) : (
              <>
                {prospectName ? `${prospectName}, we` : "We"} built CompliSun for exactly that moment.
                <strong className="text-ink-900"> A platform and a fractional team</strong> that take the
                compliance stress off your shoulders — so you can focus on the deals, the product, and the team.
              </>
            )}
          </p>

          {/* Stat callouts — law-firm specific */}
          {pack.lawFirm && pack.titleStats && (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl" data-testid="title-stats">
              {pack.titleStats.map((s) => (
                <div key={s.source} className="border border-sun-500/40 bg-white/70 rounded-md px-4 py-3">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <div className="serif text-2xl md:text-3xl text-sun-700 leading-none">{s.num}</div>
                    <div className="text-sm text-ink-900/80 leading-snug">{s.label}</div>
                  </div>
                  {s.href ? (
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`title-stat-source-${s.source.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="mt-1 inline-block mono text-[9px] uppercase tracking-[0.22em] text-ink-900/55 hover:text-sun-700 underline decoration-ink-900/20 hover:decoration-sun-500 underline-offset-2 transition-colors"
                    >
                      Source: {s.source} ↗
                    </a>
                  ) : (
                    <div className="mt-1 mono text-[9px] uppercase tracking-[0.22em] text-ink-900/50">Source: {s.source}</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Inline secondary line that frames the deck */}
          <div className="mt-7 inline-flex items-center gap-3 mono text-[11px] uppercase tracking-[0.22em] text-ink-900/65 border border-ink-900/12 rounded-full px-4 py-2 bg-white/60">
            <span className="h-1.5 w-1.5 rounded-full bg-sun-500" />
            <span>The speed of AI · The safety of an enterprise compliance team</span>
          </div>

          {/* CTA strip */}
          <div className="mt-9 flex flex-wrap items-stretch gap-3" data-testid="title-cta-strip">
            {pack.lawFirm && pack.titleBadge ? (
              <div className="flex items-center gap-3 bg-white/80 border border-sun-500/50 rounded-md px-4 py-3" data-testid="title-lawfirm-badge">
                <Award className="h-5 w-5 text-sun-700 flex-shrink-0" strokeWidth={1.5} />
                <div className="text-left max-w-[280px]">
                  <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700">For law firms</div>
                  <div className="serif text-sm text-ink-900 leading-tight">{pack.titleBadge}</div>
                </div>
              </div>
            ) : (
              <a
                href="https://www.lendapi.com/blog/lendapi-names-complisun-partner-of-the-year-2025-compliance-category"
                target="_blank"
                rel="noreferrer"
                data-testid="title-award-link"
                className="flex items-center gap-3 bg-white/80 hover:bg-white border border-ink-900/12 rounded-md px-4 py-3 transition-colors group"
              >
                <Award className="h-5 w-5 text-sun-700 flex-shrink-0" strokeWidth={1.5} />
                <div className="text-left">
                  <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700">LendAPI · 2025</div>
                  <div className="serif text-sm text-ink-900 group-hover:text-sun-700 transition-colors leading-tight">
                    Partner of the Year — Compliance
                  </div>
                </div>
              </a>
            )}
            <div className="flex flex-col gap-2">
              {(corp || pack.lawFirm) && pills ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    {pills.map((p) => (
                      <div key={p.eyebrow} className="flex items-center gap-3 border border-sun-500/40 bg-sun-500/10 rounded-md px-4 py-3">
                        <div>
                          <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700">{p.eyebrow}</div>
                          <div className="serif text-sm text-ink-900 leading-tight">{p.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {pack.titlePricingFootnote && (
                    <p className="mono text-[9px] uppercase tracking-[0.22em] text-ink-900/55">
                      {pack.titlePricingFootnote}
                    </p>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3 border border-sun-500/40 bg-sun-500/10 rounded-md px-4 py-3">
                  <div>
                    <div className="mono text-[9px] uppercase tracking-[0.22em] text-sun-700">From day one</div>
                    <div className="serif text-sm text-ink-900 leading-tight">
                      $3,225 / month · 45-day rollout
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.22em] text-ink-900/65 hover:text-sun-700 transition-colors px-3"
              onClick={() => {
                const evt = new KeyboardEvent("keydown", { key: "ArrowRight" });
                window.dispatchEvent(evt);
              }}
              data-testid="title-start-walkthrough"
            >
              Start the walkthrough <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* RIGHT — Brand mark */}
        <div className="col-span-12 md:col-span-4 flex md:justify-end">
          <img
            src="/brand/complisun-mark.png"
            alt="CompliSun"
            className="h-44 md:h-56 lg:h-64 w-auto object-contain drop-shadow-[0_0_40px_rgba(245,180,0,0.4)]"
            data-testid="title-brand-mark"
          />
        </div>
      </div>
    </SlideFrame>
  );
}
